<template>
  <b-container class="page-container">
    <LoadingIndicator 
      v-if="loading"
      topic="Meeting"
    />
    <BaseMessageBlock :messages="errors" />
    <div
      v-if="!loading && meeting !== null"
      class="project-detail meeting d-flex flex-column"
    >
      <ProjectDetails
        :projectName="projectName"
        :meetingName="meeting.name"
        :projectUsers="projectUsers"
        :projectId="projectId"
        :organizationLogo="
          projectSummary.project.primaryContractor.organizationLogo
        "
        viewProjectBtn
      />
      <div 
        v-if="!isMeetingOpen" 
        class="project-title-detail mb-3"
      >
        <div class="project-specs">
          <p>
            <span class="font-weight-bold">Public Meeting Link</span> : 
            <router-link 
              :to="`/meeting/${meetingId}`"
            >
              {{ publicMeetingUrl }}
              <!-- <b-icon icon="box-arrow-up-right"></b-icon> -->
            </router-link>
            <BaseCopyButton 
              :copyContent="publicMeetingUrl"
              buttonText="Copy Link"
              icon="link-45deg"
              size="sm" 
              cssClass="ml-3"
            />
          </p>
        </div>
      </div>
      
      <!-- Attendance Section -->
      <b-row class="mb-4">
        <b-col cols="12" class="widget-col">
          <AttendanceSection
            v-if="meetingUsers != []"
            :status="meeting.status"
            :meetingUsers="meetingUsers"
            :projectTags="projectTags"
          />
        </b-col>
      </b-row>

      <!-- Comment Section -->
      <b-row class="mb-4">
        <b-col cols="12" class="widget-col">
           <MeetingComments
           v-if="meetingComments != []"
           :projectId="projectId"
           :meetingId="meetingId"
           :meetingComments="meetingComments"
           :status="meeting.status"
            />
        </b-col>
      </b-row>

      <div v-if="!isMeetingOpen">
        <!-- Actions Taken -->
        <b-row 
          v-if="meetingStats.length > 0"
          class="mb-4"
        >
          <b-col cols="12" class="widget-col">
            <MeetingActionsTaken
              :actions="newProjectMetrics"
            />
          </b-col>
        </b-row>
        
        <!-- Summary Stats -->
        <b-row 
          v-if="meetingStats.length > 0"
          class="mb-4"
        >
          <b-col
            v-for="(item, i) in meetingStats"
            :key="i"
            class="widget-col"
            cols="2"
          >
            <div class="widget">
              <b-card class border-variant="success" no-body>
                <b-card-body class="px-0">
                  <h2 class="card-title">{{ item.label }}</h2>
                  <div class="metric-text">
                    <fitty :options="{ minSize: 10, maxSize: 40 }">
                      <template v-slot:content>
                        <p>{{ item.data.length }}</p>
                      </template>
                    </fitty>
                  </div>
                </b-card-body>
              </b-card>
            </div>
          </b-col>
        </b-row>

        <!-- Meeting Metrics Section -->
        <!-- <b-row 
          v-if="metricsTabs.length > 0"
          class="mb-4"
        >
          <b-col>
            <MeetingMetrics 
              :tabs="metricsTabs"
            />
          </b-col>
        </b-row> -->
      </div>

      <!-- Survey Section -->
      <b-row 
        v-if="isMeetingOpen" 
        class="mb-4"
      >
        <b-col>
          <SurveyResults
            :monthControl="false"
            :periods="periods"
            :selectedPeriod="periodName"
            :chartData="byQuestion"
            :allowReminding="false"
            :comments="comments"
            :projectId="projectId"
            :allProjectUsers="allProjectUsers"
            :presetTags="projectTags"
            @update="updateByQuestion"
            @refresh="refreshProjectItems"
          />
        </b-col>
      </b-row>

      <div v-if="isMeetingOpen">
        <!-- Risks Section -->
        <b-row class="mb-4" v-if="risksTabs.length">
          <b-col cols="12" class="widget-col">
            <ActionItemsTabs
              title="Risks"
              addNewText="Risk"
              :items="risksTabs"
              :projectSummary="projectSummary"
              :projectUsers="projectUsers"
              :status="meeting.status"
              @update="loadRisks"
              @refresh="refreshProjectItems"
            />
          </b-col>
        </b-row>

        <!-- Issues Section -->
        <b-row class="mb-4" v-if="issuesTabs.length">
          <b-col cols="12" class="widget-col">
            <ActionItemsTabs
              title="Issues"
              addNewText="Issue"
              :items="issuesTabs"
              :projectSummary="projectSummary"
              :status="meeting.status"
              :projectUsers="projectUsers"
              @update="loadIssues"
              @refresh="refreshProjectItems"
            />
          </b-col>
        </b-row>

        <!-- Innovations  section -->
        <LoadingIndicator 
          v-if="loadingInnovations"
          topic="Innovations"
        />
        <b-row
          v-else-if="newInnovations.length || isMeetingOpen"
          class="mb-4"
        >
          <b-col cols="12" class="widget-col">
            <ActionItems
              type="Innovation"
              :projectMetrics="newInnovations"
              :projectId="projectId"
              :meetingId="meetingId"
              :projectUsers="projectUsers"
              :projectTags="projectTags"
              :status="meeting.status"
              @load="loadInnovations"
              @refresh="refreshProjectItems"
            />
          </b-col>
        </b-row>

        <!-- Efficiencies section -->
        <LoadingIndicator 
          v-if="loadingEfficiencies"
          topic="Efficiencies"
        />
        <b-row
          v-else-if="newEfficiencies.length || isMeetingOpen"
          class="mb-4"
        >
          <b-col cols="12" class="widget-col">
            <ActionItems
              type="Efficiency"
              :projectMetrics="newEfficiencies"
              :projectId="projectId"
              :meetingId="meetingId"
              :projectUsers="projectUsers"
              :projectTags="projectTags"
              :status="meeting.status"
              @load="loadEfficiencies"
              @refresh="refreshProjectItems"
            />
          </b-col>
        </b-row>

        <!-- Sustainability section -->
        <LoadingIndicator 
          v-if="loadingSustainability"
          topic="Sustainability"
        />
        <b-row
          v-else-if="newSustainability.length || isMeetingOpen"
          class="mb-4"
        >
          <b-col cols="12" class="widget-col">
            <ActionItems
              type="Sustainability"
              :projectMetrics="newSustainability"
              :projectId="projectId"
              :meetingId="meetingId"
              :projectUsers="projectUsers"
              :projectTags="projectTags"
              :status="meeting.status"
              @load="loadSustainability"
              @refresh="refreshProjectItems"
            />
          </b-col>
        </b-row>

        <!-- Partnership section -->
        <LoadingIndicator 
          v-if="loadingPartnership"
          topic="Partnership"
        />
        <b-row
          v-else-if="newPartnership.length || isMeetingOpen"
          class="mb-4"
        >
          <b-col cols="12" class="widget-col">
            <ActionItems
              type="Partnership"
              :projectMetrics="newPartnership"
              :projectId="projectId"
              :meetingId="meetingId"
              :projectUsers="projectUsers"
              :projectTags="projectTags"
              :status="meeting.status"
              @load="loadPartnership"
              @refresh="refreshProjectItems"
            />
          </b-col>
        </b-row>
      </div>

      <!-- Meeting Notes + Documents -->
      <b-row
        class="mb-4"
      >
        <b-col cols="12" class="widget-col">
          <div class="widget" style="z-index: 99; overflow: visible">
            <div class="widget-label">
              <div class="label">
                <p>Meeting Documents</p>
              </div>
            </div>

            <div class="widget-content">
              <b-row>
                <!-- Meeting Notes -->
                <b-col
                  class="widget-col d-flex flex-column"
                  :class="
                    isMeetingOpen || meetingDocuments.length
                      ? 'pr-4 border-right'
                      : ''
                  "
                >
                  <MeetingNotes
                    v-if="meetingNotes != []"
                    :projectId="projectId"
                    :meetingId="meetingId"
                    :meetingComments="meetingNotes"
                    :isMeetingOpen="this.isMeetingOpen"
                  />
                </b-col>
                <!-- Meeting Documents -->
                <b-col
                  class="pl-4 widget-col"
                  v-if="meetingDocuments.length || isMeetingOpen"
                >
                  <div v-if="isMeetingOpen">
                    <div>
                      <p class="label" style="text-align: left; color: #525456">
                        Add Documents
                      </p>
                    </div>

                    <b-form-file
                      id="meeting-documents-upload"
                      v-model="meetingDocumentsForm.files"
                      title="Select 1 or more files"
                      multiple
                      class="mb-2"
                    >
                      <template slot="file-name" slot-scope="{ names }">
                        <b-badge variant="dark">{{ names[0] }}</b-badge>
                        <b-badge
                          v-if="names.length > 1"
                          variant="dark"
                          class="ml-1"
                          >+ {{ names.length - 1 }} More files</b-badge
                        >
                      </template>
                    </b-form-file>

                    <div class="pb-4 d-flex justify-content-end border-bottom">
                      <b-button
                        class="mr-3 text-button"
                        @click="resetUploadedDocuments"
                        >Reset</b-button
                      >
                      <b-button
                        variant="primary"
                        @click="uploadDocuments"
                        :disabled="!requestComplete"
                        class
                      >
                        <div v-if="requestComplete">Upload</div>
                        <b-spinner
                          small
                          v-if="!requestComplete"
                          label="Spinning"
                        />
                      </b-button>
                    </div>
                  </div>

                  <div>
                    <div class="mt-4">
                      <p class="label" style="text-align: left; color: #525456">
                        Uploaded Documents
                      </p>
                    </div>

                    <b-list-group>
                      <b-list-group-item
                        v-for="doc in meetingDocuments"
                        :key="doc.documentId"
                      >
                        <b-link :href="doc.url">{{ doc.name }}</b-link>
                      </b-list-group-item>
                    </b-list-group>
                    <b-card-text
                      v-if="!meetingDocuments.length"
                      style="margin: 20px"
                    >
                      <p>Upload a document!</p>
                    </b-card-text>
                  </div>
                </b-col>
              </b-row>
            </div>
          </div>
        </b-col>
      </b-row>

      <LoadingIndicator 
        v-if="loadingScheduledMeetings"
        topic="Issues"
      />
      <b-row v-else-if="isMeetingOpen" class="mb-4">
        <b-col cols="12" class="widget-col">
          <div class="widget" style="z-index: 99; overflow: visible">
            <div class="widget-label">
              <div class="label">
                <p>Schedule Meeting</p>
              </div>
              <b-button
                @click="addMeeting"
                variant="primary"
                class="right-align"
                >Schedule New Meeting</b-button
              >
            </div>

            <div class="widget-content">
              <MeetingsList
                ref="meeting-list"
                :meetings="scheduledMeetings"
                :projectId="projectId"
                :users="projectUsers"
                :allowAdd="true"
                @update="refreshScheduledMeetings"
              />
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Send Facilitator Survey -->
      <b-row>
        <b-col cols="12" class="my-4 text-center">
          <FacilitatorSurveyButton
            :meetingId="meetingId"
            :survey="facilitatorSurvey"
            :users="projectUsers"
            :attendees="attendees"
            @error="handleError"
          />
        </b-col>
      </b-row>

      <!-- Complete Meeting -->
      <b-row>
        <b-col cols="12" class="my-4 text-center">
          <b-button
            v-if="isMeetingOpen"
            variant="primary"
            @click="submitMeeting"
            :disabled="!requestComplete"
            style="width: 160px"
            >Complete Meeting</b-button
          >
        </b-col>
      </b-row>
    </div>
  </b-container>
</template>

<script>
import axios from "axios";
import { GChart } from "vue-google-charts";

import BaseCopyButton from "@/components/BaseCopyButton.vue";
import AttendanceSection from "@/components/meeting/AttendanceSection.vue";
import ProjectDetails from "@/components/project/ProjectDetails.vue";
import SurveyResults from "@/components/survey/SurveyResults.vue";
import ActionItemsTabs from "@/components/meeting/ActionItemsTabs.vue";
import ActionItems from "@/components/meeting/ActionItems.vue";
import AttendeesList from "@/components/meeting/AttendeesList.vue";
import MeetingComments from "@/components/meeting/MeetingComments.vue";
import MeetingNotes from "@/components/meeting/MeetingNotes.vue";
import MeetingMetrics from "@/components/meeting/MeetingMetrics.vue";
import MeetingsList from "@/components/project/MeetingsList.vue";
import MeetingActionsTaken from "@/components/MeetingActionsTaken.vue";
import InnovationEfficiencySection from "@/components/project/InnovationEfficiencySection.vue";
import ProjectMetricList from "@/components/project/ProjectMetricList.vue";
import FacilitatorSurveyButton from "@/components/meeting/FacilitatorSurveyButton.vue";

export default {
  components: {
    BaseCopyButton,
    AttendanceSection,
    ProjectDetails,
    SurveyResults,
    AttendeesList,
    MeetingComments,
    MeetingNotes,
    MeetingMetrics,
    MeetingsList,
    MeetingActionsTaken,
    GChart,
    ActionItems,
    ActionItemsTabs,
    InnovationEfficiencySection,
    ProjectMetricList,
    FacilitatorSurveyButton,
  },
  data() {
    return {
      //SurveyResults
      periods: [],
      byQuestionData: [],
      comments: [],

      // General
      allProjectUsers: [],
      errors: [],
      projectId: null,
      periodName: null,
      projectName: null,
      projectSummary: null,
      projectMetrics: [],
      newProjectMetrics: [],
      closedProjectMetrics: [],
      projectTags: [],
      meetingId: "",
      meeting: null,
      currentAuthor: null,
      projectUsers: [],
      meetingComments: [],
      meetingNotes: [],
      meetingUsers: [],
      meetingDocuments: [],
      facilitatorSurvey: null,
      surveyId: null,
      showAddAttendeeForm: true,
      loadingProject: false,
      loadingMeeting: false,
      loadingRisks: false,
      loadingIssues: false,
      loadingEfficiencies: false,
      loadingInnovations: false,
      loadingSustainability: false,
      loadingPartnership: false,
      loadingScheduledMeetings: false,
      scheduledMeetings: [],
      addAttendeeForm: {
        name: "",
        email: "",
        role: "Owner",
        phone: "",
      },
      meetingDocumentsForm: {
        files: [],
      },
      uploadPercentage: 0,
      requestComplete: true,
      loading: false,
      sending: false,
      byQuestion: [],
      byQuestionOptions: {
        width: "100%",
        height: "400",
        bars: "vertical",
        legend: {
          position: "none",
        },
        chart: {
          title: "Current Month",
          subtitle: "Average Scores",
        },
        hAxis: {
          slantedText: true,
          slantedTextAngle: 45,
        },
        chartArea: {
          left: 50,
          top: 10,
          bottom: 100,
          width: "100%",
          height: "50%",
        },
        vAxis: {
          format: "0.0",
          title: "Rating (scale of 0-4)",
          ticks: [0.0, 1.0, 2.0, 3.0, 4.0],
          theme: "material",
          textPosition: "out",
        },
      },
      newRisk: false,
      newIssue: false,
    };
  },
  mounted() {
    this.loading = true;
    this.projectId = this.$route.params.id;
    this.meetingId = this.$route.params.meetingId;
    if (!this.projectId || !this.meetingId) {
      this.errors.push("Meeting could not be found");
      this.loading = false;
      return;
    }

    this.loadProjectData();
    this.loadMeetingData();
    this.loadAllUsers();
  },
  methods: {
    loadProjectData() {
      let url = `${this.$api_url}/projects/${this.projectId}/summary?guest=true`;
      this.loadingProject = true;
      
      axios
        .get(url, this.getReqConfig())
        .then((response) => {
          this.setupProjectData(response.data);
          // console.log('Project Data:');
          // console.log(response.data);
          if (this.meeting) {
            this.setupMeetingUsers(this.projectUsers);
          }
        })
        .catch((err) => {
          this.errors.push(err);
        })
        .finally(() => {
          this.loadingProject = false;
          if (!this.loadingProject && !this.loadingMeeting) {
            this.loading = false;
          }
        });
    },
    loadMeetingData() {
      let url = `${this.$api_url}/meetings/${this.meetingId}`;
      this.loadingMeeting = true;
      axios
        .get(url, this.getReqConfig())
        .then((response) => {
          console.log('Meeting Data:');
          console.log(response.data);
          this.setupMeetingData(response.data);
          if (this.projectUsers) {
            this.setupMeetingUsers(this.projectUsers);
          }
        })
        .catch((err) => {
          this.errors.push(err);
        })
        .finally(() => {
          this.loadingMeeting = false;
          if (!this.loadingProject && !this.loadingMeeting) {
            this.loading = false;
          }
        });
    },
    setupProjectData(projectSummary) {
      //this.projectId = projectSummary.project.projectId;
      this.projectName = projectSummary.project.projectName;
      this.periodName = projectSummary.periodName;
      this.projectTags = projectSummary.project.presetTags;
      this.projectMetrics = projectSummary.projectMetrics;
      this.projectUsers = projectSummary.projectUsers;
      this.surveyId = projectSummary.project.survey.surveyId;

      this.projectSummary = projectSummary;

      this.comments = projectSummary.comments
        .sort((a, b) => (a.answerIndex < b.answerIndex ? 1 : -1))
        .sort((a, b) => (a.questionText > b.questionText ? 1 : -1));

      this.scheduledMeetings = projectSummary.meetings.filter(
        (m) =>
          m.periodName !== projectSummary.periodName &&
          m.start !== null &&
          new Date(m.start) > new Date()
      );

      this.byQuestion = this.$helpers.mapDataPointToChartDataSet(
        projectSummary.responsesByQuestion
      );
    },
    loadAllUsers() {
      axios
        .get(`${this.$api_url}/projects/${this.projectId}/users?inactiveUsers=true`, this.getReqConfig())
        .then(response => {
          this.allProjectUsers = response.data;
        })
        .catch(err => {
          this.errors.push(...this.$helpers.formatErrors(err));
        });
    },
    loadProjectMetrics(next) {
      axios
        .get(
          `${this.$api_url}/projects/${this.projectId}/project-metrics`,
          this.getReqConfig()
        )
        .then((response) => {
          this.projectMetrics = response.data;

          console.log('loadProjectMetrics');
          console.log(this.projectMetrics);
          if (next) {
            next();
          }
        })
        .catch((err) => {
          this.errors.push(...this.$helpers.formatErrors(err));
        });
    },
    loadMeetingProjectMetrics(next, field) {
      let url = `${this.$api_url}/meetings/${this.meetingId}/project-metrics?field=${field}`;

      axios
        .get(url, this.getReqConfig())
        .then((response) => {
          if (field === "NewProjectMetrics") {
            this.newProjectMetrics = response.data;
          } else if (field === "ClosedProjectMetrics") {
            this.closedProjectMetrics = response.data;
          }
          if (next) {
            next();
          }
        })
        .catch((err) => {
          this.errors.push(...this.$helpers.formatErrors(err));
        });
    },
    loadMeetingDocuments() {
      let url = `${this.$api_url}/documents?table=Meetings&tableId=${this.meetingId}`;
      axios
        .get(url, this.getReqConfig())
        .then((response) => {
          this.domeetingDocuments = response.data;
        })
        .catch((err) => {
          this.errors.push(...this.$helpers.formatErrors(err));
        });
    },
    loadInnovations() {
      this.loadingInnovations = true;
      this.loadMeetingProjectMetrics(() => {
        this.loadingInnovations = false;
      }, "NewProjectMetrics");
    },
    loadEfficiencies() {
      this.loadingEfficiencies = true;
      this.loadMeetingProjectMetrics(() => {
        this.loadingEfficiencies = false;
      }, "NewProjectMetrics");
    },
    loadSustainability() {
      this.loadingSustainability = true;
      this.loadMeetingProjectMetrics(() => {
        this.loadingSustainability = false;
      }, "NewProjectMetrics");
    },
    loadPartnership() {
      this.loadingPartnership = true;
      this.loadMeetingProjectMetrics(() => {
        this.loadingPartnership = false;
      }, "NewProjectMetrics");
    },
    loadNewMeetingRisks() {
      this.loadingRisks = true;
      this.loadMeetingProjectMetrics(() => {
        this.loadingRisks = false;
      }, "NewProjectMetrics");
    },
    loadClosedMeetingRisks() {
      this.loadingRisks = true;
      this.loadProjectMetrics(() => {
        this.loadMeetingProjectMetrics(() => {
          this.loadingRisks = false;
        }, "ClosedProjectMetrics");
      });
    },
    loadNewMeetingIssues() {
      this.loadingIssues = true;
      this.loadMeetingProjectMetrics(() => {
        this.loadingIssues = false;
      }, "NewProjectMetrics");
    },
    loadClosedMeetingIssues() {
      this.loadingIssues = true;
      this.loadProjectMetrics(() => {
        this.loadMeetingProjectMetrics(() => {
          this.loadingIssues = false;
        }, "ClosedProjectMetrics");
      });
    },
    setupMeetingUsers(projectUsers) {
      let usersFromProjectUsers = projectUsers.map((u) => ({
        userId: u.userId,
        name: u.name,
        email: u.email,
        role: u.role,
        jobTitle: u.jobTitle,
        organizationLogo: u.organizationLogo,
        permissionLevel: u.permissionLevel,
        attending: false,
        actionInProgress: false,
      }));

      let usersFromMeeting = this.meeting.attendees.map((a) => ({
        userId: a.userId,
        name: a.name,
        email: a.email,
        role: a.role,
        jobTitle: a.jobTitle,
        organizationLogo: a.organizationLogo,
        permissionLevel: a.permissionLevel,
        attending: true,
        actionInProgress: false,
      }));
      let attendeeIds = usersFromMeeting.map((u) => u.userId);

      //Merge the two lists and filter out the respondents who are attending
      this.meetingUsers = usersFromMeeting.concat(
        usersFromProjectUsers.filter((u) => !attendeeIds.includes(u.userId))
      );
    },
    setupMeetingData(meeting) {
      this.meeting = meeting;
      this.newProjectMetrics = meeting.newProjectMetrics;
      this.closedProjectMetrics = meeting.closedProjectMetrics;
      this.meetingDocuments = meeting.documents;
      this.facilitatorSurvey = meeting.facilitatorSurvey;
      this.meetingComments = meeting.comments.sort((a, b) => (a.dateCreated < b.dateCreated ? 1 : -1));
      if(this.meetingComments != null){
         this.dateFormat(this.meetingComments)
       }
      this.meetingNotes = meeting.notes.sort((a, b) => (a.date < b.date ? 1 : -1));
      if(this.meetingNotes != null){
        this.dateFormat(this.meetingNotes)
      }
    },
    dateFormat(commentDates) {
      if(commentDates != null){
        let newData = [];
          let line = [];
        commentDates.forEach((value, index ) => {
          value.dateCreated = new Date(value.dateCreated).setHours(new Date(value.dateCreated).getHours() - 8);
          newData.push(value);
          line.unshift(
          newData.toLocaleString("default", {
            hour: "2-digit",
            minute: "2-digit"
          })
          + ", " +
          newData.toLocaleString("default", {
            month: "long",
            day: "numeric",
            year: "numeric",
          }));
        });
      return line;
      }
      return;
    },
    handleAttendeeAdd(user) {
      user.actionInProgress = true;
      axios
        .post(
          `${this.$api_url}/meetings/${this.meeting.meetingId}/add-attendee/${user.userId}`,
          null,
          this.getReqConfig()
        )
        .then(() => {
          user.attending = true;
        })
        .catch((err) => {
          this.errors.push(err);
        })
        .finally(() => {
          user.actionInProgress = false;
        });
    },
    handleAttendeeRemove(user) {
      user.actionInProgress = true;
      axios
        .post(
          `${this.$api_url}/meetings/${this.meeting.meetingId}/delete-attendee/${user.userId}`,
          null,
          this.getReqConfig()
        )
        .then(() => {
          user.attending = false;
        })
        .catch((err) => {
          this.errors.push(err);
        })
        .finally(() => {
          user.actionInProgress = false;
        });
    },
    uploadDocuments() {
      if (this.meetingDocumentsForm.files.length > 0) {
        this.requestComplete = false;
      }
      this.meetingDocumentsForm.files.forEach((file, i, files) => {
        let documentId = this.$helpers.generateUUID();

        //Post the documents
        let formData = new FormData();
        formData.append("file", file);
        formData.append("documentId", documentId);
        formData.append("documentType", "Meeting-Document");
        formData.append("name", file.name);
        formData.append("fileName", file.name);
        formData.append("contentType", file.type);
        formData.append("tableName", "Meetings");
        formData.append("tableId", this.meeting.meetingId);

        axios
          .post(
            `${this.$api_url}/documents/upload`,
            formData,
            this.getReqConfig({
              "Content-Type": "multipart/form-data",
            })
          )
          .then(() => {
            this.loadMeetingDocuments();
            if (i === files.length - 1) {
              this.requestComplete = true;
              this.resetUploadedDocuments();
            }
          })
          .catch((err) => {
            this.errors.push(err);
            //Inform them a document didn't upload
          })
          .finally(() => {
            this.requestComplete = true;
            this.resetUploadedDocuments();
          });
      });
    },
    resetUploadedDocuments() {
      this.meetingDocumentsForm.files = [];
    },
    submitMeeting() {
      let request = {
        meetingId: this.meeting.meetingId,
        status: "Closed"
      };

      //Post the meeting
      axios
        .post(`${this.$api_url}/meeting/update`, request, this.getReqConfig())
        .then(() => {
          this.$router.push({
            name: "ProjectOverview",
            params: { id: this.projectId },
          });
        })
        .catch((err) => {
          this.errors.push(err);
        });
    },
    refreshScheduledMeetings() {
      this.loadingScheduledMeetings = true;
      axios
        .get(
          `${this.$api_url}/projects/${this.projectId}/scheduled-meeting-snippets`,
          this.getReqConfig()
        )
        .then((response) => {
          this.scheduledMeetings = response.data;
        })
        .catch((err) => {
          this.errors.push(...this.$helpers.formatErrors(err));
        })
        .finally(() => {
          this.loadingScheduledMeetings = false;
        });
    },
    refreshProjectItems(query) {
      if (query === undefined) {
        query = {
          name: "project-metrics",
        };
      }

      if (query.name === "project-metrics") {
        if (query.id === undefined) {
          let url = `${this.$api_url}/projects/${this.projectId}/project-metrics`;
          axios
            .get(url, this.getReqConfig())
            .then((response) => {
              this.projectMetrics = response.data;
            })
            .catch((err) => {
              if (err.response && err.response.statusText) {
                this.errors.push({ message: err.response.statusText });
              } else {
                this.errors.push(err);
              }
            });
        } else {
          let url = `${this.$api_url}/project-metrics/${query.id}`;
          axios
            .get(url, this.getReqConfig())
            .then((response) => {
              let index = this.projectMetrics.findIndex(
                (p) => p.projectMetricId === query.id
              );
              if (index !== -1) {
                this.projectMetrics.splice(index, 1, response.data);
                if (query.new) {
                  let newIndex = this.newProjectMetrics.findIndex(
                    (p) => p.projectMetricId === query.id
                  );
                  this.newProjectMetrics.splice(newIndex, 1, response.data);
                }
                if (query.close) {
                  let closedIndex = this.closedProjectMetrics.findIndex(
                    (p) => p.projectMetricId === query.id
                  );
                  this.closedProjectMetrics.splice(
                    closedIndex,
                    1,
                    response.data
                  );
                }
              }
            })
            .catch((err) => {
              if (err.response && err.response.statusText) {
                this.errors.push({ message: err.response.statusText });
              } else {
                this.errors.push(err);
              }
            });
        }
      } else if (query.name === "news-links") {
        let url = `${this.$api_url}/projects/${this.projectId}/news-links`;
        axios
          .get(url, this.getReqConfig())
          .then((response) => {
            this.news = response.data;
          })
          .catch((err) => {
            if (err.response && err.response.statusText) {
              this.errors.push({ message: err.response.statusText });
            } else {
              this.errors.push(err);
            }
          });
      }
    },
    updateByQuestion(update) {
      //Surveys by question
      let url = `${this.$api_url}/surveys/${this.projectSummary.project.survey.surveyId}/data-by-question?period=${update.period}`;
      axios
        .get(url, this.getReqConfig())
        .then((response) => {
          this.byQuestion = this.$helpers.mapDataPointToChartDataSet(
            response.data
          );
        })
        .catch((err) => {
          if (err.response && err.response.statusText) {
            this.errors.push({ message: err.response.statusText });
          } else {
            this.errors.push(err);
          }
        });

      //Respondent list
      url = `${this.$api_url}/projects/${this.projectId}/users?periodId=${update.period}&inactiveUsers=true`;
      axios
        .get(url, this.getReqConfig())
        .then((response) => {
          this.projectUsers = response.data;
        })
        .catch((err) => {
          if (err.response && err.response.statusText) {
            this.errors.push({ message: err.response.statusText });
          } else {
            this.errors.push(err);
          }
        });
      this.refreshComments(update);
    },
    refreshComments(update) {
      if (update.hasOwnProperty("period")) {
        let url = `${this.$api_url}/surveys/${this.projectSummary.project.survey.surveyId}/responses/comments?period=${update.period}`;
        axios
          .get(url, this.getReqConfig())
          .then((response) => {
            this.comments = response.data;
          })
          .catch((err) => {
            if (err.response && err.response.statusText) {
              this.errors.push({ message: err.response.statusText });
            } else {
              this.errors.push(err);
            }
          });
      }
    },
    loadRisks(data) {
      this.newRisk = true;
      this.loadNewMeetingRisks();
      this.loadClosedMeetingRisks();
    },
    loadIssues(data) {
      this.newIssue = true;
      this.loadNewMeetingIssues();
      this.loadClosedMeetingIssues();
    },
    loadItems(type) {
      switch (type) {
        case "innovation":
          return this.loadInnovations();
        case "efficiencies":
          return this.loadEfficiencies();
        case "sustainability":
          return this.loadSustainability();
        case "partnership":
          return this.loadPartnership();
      }
    },
    addMeeting() {
      this.$refs["meeting-list"].$refs["add-meeting-modal"].show();
    },
    openModal(ref) {
      this.$refs[ref].show();
    },
    addToMeetingStats(data, arr, label) {
      if (data.length) return arr.push({ label: label, data: data });
    },
    handleError(err) {
      this.errors.push(...this.$helpers.formatErrors(err));
    },
    resetCommentForm(){
      return {
        commentId: '',
        tenantId: '',
        projectId: '',
        meetingId: '',
        comment: '',
        commentTitle: '',
        surveyId: '',
        dateCreated: null,
        dateEdited: null,
        favorited: false,
        commentHidden: false,
        typeOfComment: '',
        userId: '',
      }
    },
    addComment(comment){
      if (comment.length === 0) {
        return;
      }
      var tmpTenantId = localStorage.getItem("tenantId");
      var tmpUserId = JSON.parse(localStorage.getItem("user"));
      var response = {
        commentId: this.$helpers.generateUUID(),
        tenantId: tmpTenantId,
        projectId: this.projectId,
        meetingId: this.meetingId,
        comment: comment,
        commentTitle: 'Meeting Comment',
        surveyId: this.surveyId,
        dateCreated: null,
        dateEdited: null,
        favorited: true,
        commentHidden: false,
        typeOfComment: 'Meeting Comment',
        userId: tmpUserId.userId,
      }
       this.sending = true;
      axios
        .post(
          `${this.$api_url}/meeting/${this.meeting.meetingId}/comments/add`,
          response,
          this.getReqConfig()
        )
        .then(() => {
          this.commentForm = this.resetCommentForm();
          this.$emit("refresh", this.projectMetric.projectMetricId);
        })
        .catch(() => {
          //Do something about it, yo
        })
        .finally(() => {
          this.sending = false;
        });
    },
  },
  computed: {
    isMeetingOpen() {
      return this.meeting.status !== "Closed";
    },
    publicMeetingUrl() {
      return `${window.location.origin}/meeting/${this.meetingId}`;
    },
    risksTabs() {
      return [
        {
          title: "Open Risks",
          noneText: "No open risks",
          icon: "list-check",
          action: "Close",
          viewByLink: true,
          projectMetrics: this.openRisks,
          update: this.loadClosedMeetingRisks,
          ref: "all-open-risks",
        },
        {
          title: "New Risks",
          addTitle: "New Risk",
          noneText: "No new risks",
          icon: "list-check",
          allowAdd: true,
          addTypes: ["Risk"],
          possibleAssignees: this.projectUsers,
          projectMetrics: this.newRisks,
          update: this.loadNewMeetingRisks,
          ref: "all-new-risks",
          active: this.newRisk,
        },
        {
          title: "Closed Risks",
          noneText: "No closed risks",
          icon: "list-check",
          action: "Undo",
          viewByLink: true,
          projectMetrics: this.closedRisks,
          update: this.loadClosedMeetingRisks,
          ref: "all-closed-risks",
        },
      ];
    },
    issuesTabs() {
      return [
        {
          title: "Open Issues",
          noneText: "No open issues",
          action: "Close",
          viewByLink: true,
          projectMetrics: this.openIssues,
          update: this.loadClosedMeetingIssues,
        },
        {
          title: "New Issues",
          addTitle: "New Issue",
          noneText: "No new issues",
          allowAdd: true,
          addTypes: ["Issue"],
          possibleAssignees: this.projectUsers,
          projectMetrics: this.newIssues,
          update: this.loadNewMeetingIssues,
          active: this.newIssue,
        },
        {
          title: "Closed Issues",
          noneText: "No closed issues",
          action: "Undo",
          viewByLink: true,
          projectMetrics: this.closedIssues,
          update: this.loadClosedMeetingIssues,
        },
      ];
    },
    metricsTabs() {
      const possibleMetricsTabs = [
        {
          name: "risks",
          title: "Risks",
          icon: "cone",
          items: this.newRisks,
          itemsCount: this.newRisks.length
        },
        {
          name: "issues",
          title: "Issues",
          icon: "exclamation-circle",
          items: this.newIssues,
          itemsCount: this.newIssues.length
        },
        {
          name: "innovations",
          title: "Innovations",
          icon: "lamp-fill",
          items: this.newInnovations,
          itemsCount: this.newInnovations.length
        },
        {
          name: "efficiencies",
          title: "Efficiencies",
          icon: "hourglass-split",
          items: this.newEfficiencies,
          itemsCount: this.newEfficiencies.length
        },
        {
          name: "sustainability",
          title: "Sustainability",
          icon: "lightning-fill",
          items: this.newSustainability,
          itemsCount: this.newSustainability.length
        },
        {
          name: "partnership",
          title: "Partnership",
          icon: "people-fill",
          items: this.newPartnership,
          itemsCount: this.newPartnership.length
        },
      ];
      return possibleMetricsTabs.filter(tab => tab.itemsCount > 0);
    },
    newRisks() {
      return this.newProjectMetrics.filter((a) => a.type === "Risk");
    },
    newIssues() {
      return this.newProjectMetrics.filter((a) => a.type === "Issue");
    },
    openRisks() {
      return this.projectMetrics.filter(
        (a) => a.status === "Open" && a.type === "Risk"
      );
    },
    openIssues() {
      return this.projectMetrics.filter(
        (a) => a.status === "Open" && a.type === "Issue"
      );
    },
    closedRisks() {
      return this.closedProjectMetrics.filter((a) => a.type === "Risk");
    },
    closedIssues() {
      return this.closedProjectMetrics.filter((a) => a.type === "Issue");
    },
    associatedUsers() {
      return [];
    },
    newInnovations() {
      return this.newProjectMetrics.filter((a) => a.type.includes("Innovation"));
    },
    newEfficiencies() {
      return this.newProjectMetrics.filter((a) => a.type.includes("Efficiency"));
    },
    newSustainability() {
      return this.newProjectMetrics.filter((a) => a.type.includes("Sustainability"));
    },
    newPartnership() {
      return this.newProjectMetrics.filter((a) => a.type.includes("Partnership"));
    },
    meetingStats() {
      let meetingArr = [];

      this.addToMeetingStats(this.newRisks, meetingArr, "New Risks");
      this.addToMeetingStats(this.closedRisks, meetingArr, "Closed Risks");
      this.addToMeetingStats(this.newIssues, meetingArr, "New Issues");
      this.addToMeetingStats(this.closedIssues, meetingArr, "Closed Issues");
      this.addToMeetingStats(
        this.newInnovations,
        meetingArr,
        "New Innovations"
      );
      this.addToMeetingStats(
        this.newEfficiencies,
        meetingArr,
        "New Efficiencies"
      );
      this.addToMeetingStats(
        this.newSustainability,
        meetingArr,
        "New Sustainabilities"
      );
      this.addToMeetingStats(
        this.newPartnership,
        meetingArr,
        "New Partnerships"
      );
      this.addToMeetingStats(
        this.meetingDocuments,
        meetingArr,
        "New Documents"
      );

      return meetingArr;
    },
    attendees() {
      return this.meetingUsers.filter((u) => u.attending);
    },
  },
};
</script>

<style lang="scss" >
.surveyResults {
  font-size: 2em;
  line-height: 0.75em;
}
.surveyTitle {
  font-size: 1.75em;
  line-height: 1em;
  color: #555;
}
.periodTitle {
  font-size: 1.5em;
  line-height: 0.5em;
  color: #555;
  font-style: normal;
}
.no-card {
  .card-body {
    padding: 0;
  }
}
</style>